import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Select,
  Button,
  Table,
  Tag,
  Statistic,
  Alert,
  Divider,
  Space,
  Spin,
  Empty
} from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  SwapOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { useVersions, useCompareVersions } from '../../hooks/useEstimation';
import { useCurrency } from '@/contexts/CurrencyContext';
import { AedDirhamSign } from '@/components/currency';

const { Option } = Select;

export const VersionComparisonPage: React.FC = () => {
  const { formatCurrency, baseCurrency } = useCurrency();
  const { estimationId } = useParams<{ estimationId: string }>();
  const [version1Id, setVersion1Id] = useState<string>('');
  const [version2Id, setVersion2Id] = useState<string>('');
  const [comparisonData, setComparisonData] = useState<any>(null);

  // Queries
  const { data: versionsData, isLoading: versionsLoading } = useVersions(estimationId || '');
  const compareVersionsMutation = useCompareVersions();

  const versions = Array.isArray(versionsData)
    ? versionsData
    : (versionsData as { data?: unknown[] })?.data ?? [];

  // Handle comparison
  const handleCompare = async () => {
    if (!version1Id || !version2Id) return;

    try {
      const result = await compareVersionsMutation.mutateAsync({ version1Id, version2Id });
      const payload = result as { data?: unknown };
      setComparisonData(payload?.data ?? result);
    } catch (error) {
      console.error('Failed to compare versions', error);
    }
  };

  // Render delta indicator
  const renderDelta = (delta: number, percentDelta: number, isReversed: boolean = false) => {
    const isPositive = delta > 0;
    const isFavorable = isReversed ? !isPositive : isPositive;
    const color = isFavorable ? '#52c41a' : '#ff4d4f';
    const Icon = isPositive ? ArrowUpOutlined : ArrowDownOutlined;

    return (
      <Space>
        <span style={{ color }}>{delta > 0 ? '+' : ''}{delta.toLocaleString()}</span>
        <Tag color={isFavorable ? 'success' : 'error'} icon={<Icon />}>
          {percentDelta > 0 ? '+' : ''}{percentDelta.toFixed(1)}%
        </Tag>
      </Space>
    );
  };

  // Cost breakdown columns
  const costBreakdownColumns = [
    {
      title: 'Cost Category',
      dataIndex: 'category',
      key: 'category',
      width: 200
    },
    {
      title: `Version ${comparisonData?.version1.versionNumber || 1}`,
      dataIndex: 'v1',
      key: 'v1',
      render: (value: number) => formatCurrency(value, baseCurrency?.currency_code || 'AED'),
      width: 150
    },
    {
      title: `Version ${comparisonData?.version2.versionNumber || 2}`,
      dataIndex: 'v2',
      key: 'v2',
      render: (value: number) => formatCurrency(value, baseCurrency?.currency_code || 'AED'),
      width: 150
    },
    {
      title: 'Delta',
      key: 'delta',
      render: (_: any, record: any) => {
        const delta = record.v2 - record.v1;
        const percentDelta = record.v1 > 0 ? ((delta / record.v1) * 100) : 0;
        return renderDelta(delta, percentDelta, true); // true = lower is better
      }
    }
  ];

  // Prepare cost breakdown data
  const costBreakdownData = comparisonData
    ? [
        {
          key: 'material',
          category: 'Material Cost',
          v1: comparisonData.version1.totalMaterialCost,
          v2: comparisonData.version2.totalMaterialCost
        },
        {
          key: 'labor',
          category: 'Labor Cost',
          v1: comparisonData.version1.totalLaborCost,
          v2: comparisonData.version2.totalLaborCost
        },
        {
          key: 'overhead',
          category: 'Overhead Cost',
          v1: comparisonData.version1.totalOverheadCost,
          v2: comparisonData.version2.totalOverheadCost
        },
        {
          key: 'tooling',
          category: 'Tooling Cost',
          v1: comparisonData.version1.totalToolingCost,
          v2: comparisonData.version2.totalToolingCost
        },
        {
          key: 'total',
          category: <strong>Total Cost</strong>,
          v1: comparisonData.version1.totalCost,
          v2: comparisonData.version2.totalCost
        }
      ]
    : [];

  // Changed items columns
  const changedItemsColumns = [
    {
      title: 'Item',
      dataIndex: 'itemName',
      key: 'itemName'
    },
    {
      title: 'Change Type',
      dataIndex: 'changeType',
      key: 'changeType',
      render: (type: string) => {
        let color = 'default';
        if (type === 'Added') color = 'success';
        else if (type === 'Removed') color = 'error';
        else if (type === 'Modified') color = 'warning';
        return <Tag color={color}>{type}</Tag>;
      }
    },
    {
      title: 'V1 Cost',
      dataIndex: 'v1Value',
      key: 'v1Value',
      render: (value?: number) =>
        value != null ? formatCurrency(value, baseCurrency?.currency_code || 'AED') : '-',
    },
    {
      title: 'V2 Cost',
      dataIndex: 'v2Value',
      key: 'v2Value',
      render: (value?: number) =>
        value != null ? formatCurrency(value, baseCurrency?.currency_code || 'AED') : '-',
    },
    {
      title: 'Impact',
      dataIndex: 'costImpact',
      key: 'costImpact',
      render: (value: number) => {
        const color = value < 0 ? '#52c41a' : '#ff4d4f';
        return (
          <span style={{ color }}>
            {value > 0 ? '+' : ''}
            {value.toLocaleString()}
          </span>
        );
      }
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 8 }}>
          <SwapOutlined style={{ marginRight: 8 }} />
          Version Comparison
        </h1>
        <p style={{ color: '#8c8c8c' }}>Compare two estimation versions side-by-side</p>
      </div>

      {/* Version Selection */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col span={10}>
            <div style={{ marginBottom: 8 }}>
              <strong>Version 1 (Base)</strong>
            </div>
            <Select
              style={{ width: '100%' }}
              placeholder="Select first version"
              value={version1Id}
              onChange={setVersion1Id}
              loading={versionsLoading}
            >
              {versions.map((v: any) => (
                <Option key={v.id} value={v.id}>
                  V{v.version_number} - {v.version_name} ({v.status})
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={4} style={{ textAlign: 'center' }}>
            <SwapOutlined style={{ fontSize: 24, color: '#145A54' }} />
          </Col>

          <Col span={10}>
            <div style={{ marginBottom: 8 }}>
              <strong>Version 2 (Compare)</strong>
            </div>
            <Select
              style={{ width: '100%' }}
              placeholder="Select second version"
              value={version2Id}
              onChange={setVersion2Id}
              loading={versionsLoading}
            >
              {versions.map((v: any) => (
                <Option key={v.id} value={v.id}>
                  V{v.version_number} - {v.version_name} ({v.status})
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Divider />

        <div style={{ textAlign: 'center' }}>
          <Space>
            <Button
              type="primary"
              size="large"
              icon={<SwapOutlined />}
              onClick={handleCompare}
              disabled={!version1Id || !version2Id || version1Id === version2Id}
              loading={compareVersionsMutation.isPending}
            >
              Compare Versions
            </Button>
            <Button icon={<DownloadOutlined />} disabled={!comparisonData}>
              Export Comparison
            </Button>
          </Space>
        </div>
      </Card>

      {/* Comparison Results */}
      {compareVersionsMutation.isPending ? (
        <Card style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
          <p style={{ marginTop: 16 }}>Comparing versions...</p>
        </Card>
      ) : comparisonData ? (
        <>
          {/* Overall Recommendation */}
          <Alert
            message={comparisonData.recommendation}
            type={comparisonData.costDelta.totalCostDiff < 0 ? 'success' : 'info'}
            icon={comparisonData.costDelta.totalCostDiff < 0 ? <CheckCircleOutlined /> : <WarningOutlined />}
            showIcon
            style={{ marginBottom: 24 }}
          />

          {/* Cost Delta Summary */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Material Cost Delta"
                  value={Math.abs(comparisonData.costDelta.materialCostDiff)}
                  prefix={comparisonData.costDelta.materialCostDiff < 0 ? '↓' : '↑'}
                  suffix={<AedDirhamSign />}
                  valueStyle={{
                    color: comparisonData.costDelta.materialCostDiff < 0 ? '#52c41a' : '#ff4d4f'
                  }}
                />
                <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                  {comparisonData.costDelta.materialCostPercentChange.toFixed(1)}% change
                </div>
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Labor Cost Delta"
                  value={Math.abs(comparisonData.costDelta.laborCostDiff)}
                  prefix={comparisonData.costDelta.laborCostDiff < 0 ? '↓' : '↑'}
                  suffix={<AedDirhamSign />}
                  valueStyle={{
                    color: comparisonData.costDelta.laborCostDiff < 0 ? '#52c41a' : '#ff4d4f'
                  }}
                />
                <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                  {comparisonData.costDelta.laborCostPercentChange.toFixed(1)}% change
                </div>
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Overhead Cost Delta"
                  value={Math.abs(comparisonData.costDelta.overheadCostDiff)}
                  prefix={comparisonData.costDelta.overheadCostDiff < 0 ? '↓' : '↑'}
                  suffix={<AedDirhamSign />}
                  valueStyle={{
                    color: comparisonData.costDelta.overheadCostDiff < 0 ? '#52c41a' : '#ff4d4f'
                  }}
                />
                <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                  {comparisonData.costDelta.overheadCostPercentChange.toFixed(1)}% change
                </div>
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Total Cost Delta"
                  value={Math.abs(comparisonData.costDelta.totalCostDiff)}
                  prefix={comparisonData.costDelta.totalCostDiff < 0 ? '↓' : '↑'}
                  suffix={<AedDirhamSign />}
                  valueStyle={{
                    color: comparisonData.costDelta.totalCostDiff < 0 ? '#52c41a' : '#ff4d4f',
                    fontSize: 28
                  }}
                />
                <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                  {comparisonData.costDelta.percentageChange.toFixed(1)}% change
                </div>
              </Card>
            </Col>
          </Row>

          {/* Side-by-Side Cost Breakdown */}
          <Card title="Cost Breakdown Comparison" style={{ marginBottom: 24 }}>
            <Table
              columns={costBreakdownColumns}
              dataSource={costBreakdownData}
              pagination={false}
              size="middle"
            />
          </Card>

          {/* Significant Changes */}
          {comparisonData.significantChanges && comparisonData.significantChanges.length > 0 && (
            <Card title="Significant Changes (>10%)" style={{ marginBottom: 24 }}>
              {comparisonData.significantChanges.map((change: any, index: number) => (
                <Alert
                  key={index}
                  message={change.description}
                  description={`Impact: ${change.impact} | Cost Delta: ${formatCurrency(
                    change.costDelta,
                    baseCurrency?.currency_code || 'AED'
                  )} (${change.percentageDelta.toFixed(1)}%)`}
                  type={change.impact === 'High' ? 'error' : change.impact === 'Medium' ? 'warning' : 'info'}
                  showIcon
                  style={{ marginBottom: 12 }}
                />
              ))}
            </Card>
          )}

          {/* Changed Items */}
          {comparisonData.changedItems && comparisonData.changedItems.length > 0 && (
            <Card title="Changed Items">
              <Table
                columns={changedItemsColumns}
                dataSource={comparisonData.changedItems}
                rowKey="itemId"
                pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                size="small"
              />
            </Card>
          )}
        </>
      ) : (
        <Card style={{ textAlign: 'center', padding: 48 }}>
          <Empty
            description="Select two versions and click Compare to see results"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      )}
    </div>
  );
};

export default VersionComparisonPage;

